import React, { useEffect, useState } from "react"
import {
  Stack, Text, Flex, Box,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import Card from "components/layout/Card"
import CopyableLink from "components/elements/CopyableLink"
import { DioboxEvent, ModalName } from "sharedTypes"
import { EventUserRole } from "services/permissions"
import { asUsd } from "utilities/strings"
import { leadRetrievalRegisterPath } from "utilities/routes"
import Link from "components/elements/Link"
import { LEAD_RETRIEVAL_HELP_ARTICLE_LINK } from "utilities/externalLinks"
import SettingsSection from "components/settings/SettingsSection"
import Button from "components/Buttons/Button"
import useModal from "services/useModal"
import useSearchParams from "services/useSearchParams"
import PurchaseLeadRetrievalLicenses from "modals/PurchaseLeadRetrievalLicenses"
import PurchaseLeadRetrievalLicensesSuccess from "modals/PurchaseLeadRetrievalLicensesSuccess"
import { useLeadRetrievalLicenses, leadRetrievalLicensesCacheKey, eventCacheKey } from "queries/events"
import {
  revokeLeadRetrievalLicense, revokeLeadRetrievalExhibitor, cancelLeadRetrievalPurchaseInvite,
  resendLeadRetrievalInvite, resendLeadRetrievalPurchaseInvite, updateLeadRetrievalExhibitorName,
} from "api/events"
import { successToast } from "utilities/toasts"
import RoundedList from "../RoundedList"
import ExhibitorListItem from "./ExhibitorListItem/ExhibitorListItem"
import NoLeadRetrievalUserFound from "./NoLeadRetrievalUserFound"
import LeadRetrievalUpgradeAlert from "./LeadRetrievalUpgradeAlert"

type Props = {
  event: DioboxEvent
}

const LeadRetrieval = ({
  event: {
    id,
    externalId,
    users,
    leadRetrievalLicenseFee,
    leadRetrievalEnabled,
  },
}: Props) => {
  const showModal = useModal()
  const queryClient = useQueryClient()
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [purchasedQuantity, setPurchasedQuantity] = useState(0)
  const { data: summary } = useLeadRetrievalLicenses(id)
  const { showPaymentSuccessModal, quantity } = useSearchParams<{
    showPaymentSuccessModal?: string, quantity?: string
  }>()

  useEffect(() => {
    if (showPaymentSuccessModal) {
      queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(id))
      // Captured once here rather than read live from the URL elsewhere -- PaymentSuccessful
      // strips the query string shortly after mount, which would otherwise re-render this with
      // quantity undefined and flip the success modal's count down to 0.
      setPurchasedQuantity(Number(quantity) || 0)
      setShowSuccessModal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentSuccessModal])

  const totalLicenses = summary?.total ?? 0
  const usedLicenses = summary?.used ?? 0
  const remainingLicenses = summary?.remaining ?? 0
  const licenseFee = Number(leadRetrievalLicenseFee)
  const exhibitorSignUpPageUrl = `${import.meta.env.VITE_BASE}${leadRetrievalRegisterPath()}/${externalId}`

  const revokeLicense = async (licenseId: string, email: string) => {
    await revokeLeadRetrievalLicense(id, licenseId)
    await Promise.all([
      queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(id)),
      // Active exhibitors are sourced from the event's users list, not the licenses summary.
      queryClient.invalidateQueries(eventCacheKey(id)),
    ])
    successToast({ title: `Revoked invite for ${email}` })
  }

  const revokeExhibitor = async (eventAccessId: number, email: string) => {
    await revokeLeadRetrievalExhibitor(id, eventAccessId)
    await Promise.all([
      queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(id)),
      queryClient.invalidateQueries(eventCacheKey(id)),
    ])
    successToast({ title: `Removed ${email}` })
  }

  const updateExhibitorName = async (eventAccessId: number, name: string) => {
    await updateLeadRetrievalExhibitorName(id, eventAccessId, name)
    await queryClient.invalidateQueries(eventCacheKey(id))
    successToast({ title: "Company name updated" })
  }

  const activeById = new Map((summary?.active ?? []).map((a) => [a.id, a]))

  const purchasedExhibitors = users.filter(({ role }) => role === EventUserRole.LeadRetrieval).map(
    ({
      id: eventAccessId, name, email, photoUrl, licenseSource,
    }) => {
      const activeInfo = activeById.get(eventAccessId)

      return {
        name,
        email,
        photoUrl,
        sourceLabel: licenseSource === "organizer_activation"
          ? "Organizer-Paid" as const : "Exhibitor-Paid" as const,
        statusLabel: "Active" as const,
        activatedAt: activeInfo?.activatedAt,
        licenseId: eventAccessId,
        onRevoke: () => revokeExhibitor(eventAccessId, email),
        onUpdateName: (newName: string) => updateExhibitorName(eventAccessId, newName),
      }
    },
  )

  const cancelPurchaseInvite = async (inviteId: string, email: string) => {
    await cancelLeadRetrievalPurchaseInvite(id, inviteId)
    await queryClient.invalidateQueries(leadRetrievalLicensesCacheKey(id))
    successToast({ title: `Cancelled invite for ${email}` })
  }

  const resendInvite = async (licenseId: string, email: string) => {
    await resendLeadRetrievalInvite(id, licenseId)
    successToast({ title: `Resent invite to ${email}` })
  }

  const resendPurchaseInvite = async (inviteId: string, email: string) => {
    await resendLeadRetrievalPurchaseInvite(id, inviteId)
    successToast({ title: `Resent invite to ${email}` })
  }

  const organizerActivatedExhibitors = (summary?.assigned ?? []).map(
    ({
      id: licenseId, email, assignedAt, inviteToken,
    }) => ({
      name: undefined,
      email,
      sourceLabel: "Organizer-Paid" as const,
      statusLabel: "Invited" as const,
      invitedAt: assignedAt,
      inviteUrl: `${import.meta.env.VITE_BASE}${leadRetrievalRegisterPath()}/${externalId}/invite/${inviteToken}`,
      onRevoke: () => revokeLicense(licenseId, email),
      onResend: () => resendInvite(licenseId, email),
    }),
  )
  const invitedToPurchaseExhibitors = (summary?.purchaseInvites ?? []).map(
    ({ id: inviteId, email, invitedAt }) => ({
      name: undefined,
      email,
      sourceLabel: "Exhibitor-Paid" as const,
      statusLabel: "Invited" as const,
      invitedAt,
      inviteUrl: `${exhibitorSignUpPageUrl}?email=${encodeURIComponent(email)}`,
      onRevoke: () => cancelPurchaseInvite(inviteId, email),
      onResend: () => resendPurchaseInvite(inviteId, email),
    }),
  )
  const exhibitors = [
    ...organizerActivatedExhibitors,
    ...invitedToPurchaseExhibitors,
    ...purchasedExhibitors,
  ]

  return (
    <Stack spacing={12}>
      <SettingsSection
        title="Lead Retrieval"
        description={(
          <>
            <Text>
              Let exhibitors and sponsors capture leads at your event by scanning guest QR
              codes with the Lead Retrieval app on their own devices. Each exhibitor needs
              one license.
            </Text>
            <Text>
              Learn more: {" "}
              <Link isExternal href={LEAD_RETRIEVAL_HELP_ARTICLE_LINK}>
                Lead Retrieval Help Center
              </Link>
            </Text>
          </>
        )}
      >
        {leadRetrievalEnabled && (
        <Card p={0}>
          <Stack spacing={0}>
            <Flex px={6} py={6}>
              <Box w="50%">
                <Stack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {totalLicenses}
                  </Text>
                  <Text color="gray.600">Total Event Licenses</Text>
                </Stack>
              </Box>
              <Box w="25%">
                <Stack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {usedLicenses}
                  </Text>
                  <Text color="gray.600">Assigned</Text>
                </Stack>
              </Box>
              <Box w="25%">
                <Stack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {remainingLicenses}
                  </Text>
                  <Text color="gray.600">Available</Text>
                </Stack>
              </Box>
            </Flex>
            <Flex px={6} py={3} bg="blue.50" borderTopWidth={1} borderBottomRadius="md">
              <Text>
                License Fee: {" "}
                <Text as="span" fontWeight="bold">{asUsd(licenseFee)}</Text>
              </Text>
            </Flex>
          </Stack>
        </Card>
        )}
      </SettingsSection>
      {!leadRetrievalEnabled
        ? <LeadRetrievalUpgradeAlert />
        : (
          <>
            <SettingsSection
              title="License Options"
              description="Choose who pays for licenses. Both options can be used in the same event."
            >
              <Flex direction={{ base: "column", md: "row" }} gap={6} align="stretch">
                <Box flex={1}>
                  <Card h="100%">
                    <Stack spacing={4}>
                      <Text fontWeight="semibold" fontSize="lg">Organizer Pays</Text>
                      <Text>
                        Buy licenses upfront and assign them to exhibitors. Assigned exhibitors
                        get app access, at no cost to them.
                      </Text>
                      <Box>
                        <Button onClick={() => setShowPurchaseModal(true)}>
                          Purchase Licenses
                        </Button>
                      </Box>
                    </Stack>
                  </Card>
                </Box>
                <Box flex={1}>
                  <Card h="100%">
                    <Stack spacing={4}>
                      <Text fontWeight="semibold" fontSize="lg">Exhibitor Pays</Text>
                      <Text>
                        Invite exhibitors to purchase their own license directly from Diobox.
                      </Text>
                      <Box>
                        <Button
                          variant="outline"
                          onClick={() => showModal(ModalName.InviteExhibitorToPurchase)}
                        >
                          Invite to Purchase
                        </Button>
                      </Box>
                      <CopyableLink
                        label="Purchase Link"
                        value={exhibitorSignUpPageUrl}
                        helperText="Anyone with this link can purchase a license for this event."
                      />
                    </Stack>
                  </Card>
                </Box>
              </Flex>
            </SettingsSection>
            <SettingsSection
              title="Exhibitors"
              actionButtons={[
                <Button
                  key="assign-license"
                  isDisabled={remainingLicenses <= 0}
                  onClick={() => showModal(ModalName.InviteExhibitorOrAssignLicense)}
                >
                  Assign License
                </Button>,
              ]}
            >
              {exhibitors.length === 0
                ? <NoLeadRetrievalUserFound />
                : (
                  <RoundedList>
                    <Flex px={6} py={3} justifyContent="space-between" alignItems="center">
                      <Text fontWeight="semibold" w="50%">EXHIBITOR</Text>
                      <Text fontWeight="semibold" w={1 / 5}>LICENSE</Text>
                      <Text fontWeight="semibold" w={1 / 5}>STATUS</Text>
                      <Box w={20} />
                    </Flex>
                    {exhibitors.map((exhibitor) => (
                      <ExhibitorListItem
                        key={exhibitor.email}
                        eventId={id}
                        exhibitor={exhibitor}
                        licenseFee={licenseFee}
                      />
                    ))}
                  </RoundedList>
                )}
            </SettingsSection>
          </>
        )}
      {showPurchaseModal && (
        <PurchaseLeadRetrievalLicenses
          eventId={id}
          licenseFee={licenseFee}
          onHide={() => setShowPurchaseModal(false)}
        />
      )}
      {showSuccessModal && (
        <PurchaseLeadRetrievalLicensesSuccess
          quantity={purchasedQuantity}
          onHide={() => setShowSuccessModal(false)}
        />
      )}
    </Stack>
  )
}

export default LeadRetrieval
